import React, { useEffect, useState } from 'react';
import Navbar from './Features/Navbar';
import hr from '../images/icons/hr.png';
import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import Footer from './Home/Footer';
import { Fade } from 'react-awesome-reveal';
import StrapiRichText from './StrapiRichText';
import { fetchStrapiSingleType } from '../services/strapiClient';
import { pickCmsText, pickCmsTitle } from '../utils/cmsContent';

const richTextSx = {
  fontSize: '20px',
  lineHeight: 1.8,
  fontFamily: 'Montserrat',
  color: '#2f3036',
  fontWeight: 500,
  '& p': { marginTop: 0, marginBottom: '1rem' },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { marginBottom: '1rem', paddingLeft: '1.25rem' },
  '& ul ul': { marginTop: '0.5rem', marginBottom: '0.5rem' },
  '& li': { marginBottom: '0.35rem' },
  '& strong': { fontWeight: 700 },
  '& a': { color: '#1398aa' },
  '& u': { textDecoration: 'underline', fontStyle: 'italic' },
  '& h2': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '34px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& h3': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '28px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& .service-callout': {
    borderStyle: 'solid',
    borderRadius: '20px',
    backgroundColor: '#EBEAFF',
    borderColor: '#EBEAFF',
    marginBottom: '3rem',
    padding: '20px',
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '20px',
    color: '#2f3036',
    fontWeight: 600,
  },
  '& .service-callout p': { marginTop: 0, marginBottom: '1rem' },
  '& .service-callout p:last-child': { marginBottom: 0 },
};

function normalizeDerouleurs(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((entry) => {
      const o = entry?.attributes ?? entry;
      const title = o?.title != null ? String(o.title).trim() : '';
      const content = o?.content;
      return { title, content };
    })
    .filter((x) => x.title || (x.content != null && String(x.content).trim() !== ''));
}

function normalizeRich(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function hasCms(remote) {
  return Boolean(
    normalizeRich(remote?.bodyTop) ||
      normalizeRich(remote?.bodyBottom) ||
      normalizeDerouleurs(remote?.derouleurs).length > 0
  );
}

export default function AnnexeCarnetFormsPageShell({
  strapiApiId = 'annexe-carnet-forms',
  defaultSectionLabel = 'Annexes',
  defaultTitle = 'Formulaires',
  defaultDeroulementTitle = "Carnet d'adresses",
  StaticFallback,
  fadeTriggerOnce = true,
}) {
  const [sectionLabel, setSectionLabel] = useState(defaultSectionLabel);
  const [title, setTitle] = useState(defaultTitle);
  const [deroulementTitle, setDeroulementTitle] = useState(defaultDeroulementTitle);
  const [remote, setRemote] = useState(null);
  const [openDerouleurs, setOpenDerouleurs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${title} | Tsahal Connection`;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const attrs = await fetchStrapiSingleType(strapiApiId, { populate: 'populate=*' });
        if (cancelled) return;
        if (attrs) {
          setSectionLabel(pickCmsText(attrs.sectionLabel, defaultSectionLabel));
          setTitle(pickCmsTitle(attrs.title, defaultTitle));
          setDeroulementTitle(pickCmsText(attrs.deroulementTitle, defaultDeroulementTitle));
          setRemote(attrs);
          const der = normalizeDerouleurs(attrs.derouleurs);
          setOpenDerouleurs(der.length > 0 ? new Array(der.length).fill(false) : []);
        } else {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setDeroulementTitle(defaultDeroulementTitle);
          setRemote(null);
          setOpenDerouleurs([]);
        }
      } catch {
        if (!cancelled) {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setDeroulementTitle(defaultDeroulementTitle);
          setRemote(null);
          setOpenDerouleurs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [strapiApiId, defaultSectionLabel, defaultTitle, defaultDeroulementTitle]);

  const toggleDerouleur = (index) => {
    setOpenDerouleurs((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '40vh', alignItems: 'center' }}>
          <CircularProgress sx={{ color: '#1398aa' }} />
        </Box>
        <Footer />
      </div>
    );
  }

  const bodyTop = normalizeRich(remote?.bodyTop);
  const bodyBottom = normalizeRich(remote?.bodyBottom);
  const derouleurs = normalizeDerouleurs(remote?.derouleurs);
  const useCms = hasCms(remote);

  return (
    <div>
      <Navbar />
      <Fade triggerOnce={fadeTriggerOnce}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '800', fontSize: '1.2rem', marginBottom: '2rem', color: '#ae39ed', textAlign: 'center' }}>
            {sectionLabel}
          </h1>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '700', fontSize: '48px', textAlign: 'center' }}>{title}</h1>
          <img src={hr} alt="" width="80" height="50" />
        </div>
        {useCms ? (
          <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
            {bodyTop ? <StrapiRichText source={bodyTop} sx={richTextSx} /> : null}
            {derouleurs.length > 0 ? (
              <>
                <Typography
                  variant="h3"
                  style={{
                    fontFamily: 'Montserrat',
                    lineHeight: 1.8,
                    fontSize: '34px',
                    marginBottom: '1rem',
                    marginTop: '1rem',
                    fontWeight: 700,
                  }}
                >
                  {deroulementTitle}
                </Typography>
                <div style={{ marginBottom: '4rem' }}>
                  {derouleurs.map((row, index) => (
                    <div
                      key={`${row.title}-${index}`}
                      style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '1rem', marginBottom: '1rem' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography
                          variant="body1"
                          style={{
                            fontFamily: 'Montserrat',
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#2f3036',
                            textTransform: 'uppercase',
                            marginTop: '0.5rem',
                          }}
                        >
                          {row.title || '—'}
                        </Typography>
                        <Button
                          variant="text"
                          onClick={() => toggleDerouleur(index)}
                          style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: 500, color: '#2f3036', outline: 'none' }}
                        >
                          {openDerouleurs[index] ? '-' : '+'}
                        </Button>
                      </div>
                      {openDerouleurs[index] && row.content != null && String(row.content).trim() !== '' ? (
                        <div style={{ marginTop: '1rem' }}>
                          <StrapiRichText source={row.content} sx={richTextSx} />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {bodyBottom ? <StrapiRichText source={bodyBottom} sx={richTextSx} /> : null}
          </Container>
        ) : (
          <StaticFallback />
        )}
        <Footer />
      </Fade>
    </div>
  );
}
